import { or } from 'tio-ui/utilities';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import {
  PhCheckCircle,
  PhCloudArrowUp,
  PhFilePdf,
  PhInfo,
  PhSparkle,
  PhUploadSimple,
  PhWarningCircle,
  PhX,
  PhXCircle,
} from 'ember-phosphor-icons';
import Popover from 'tio-ui/components/popover';
import FileUploader from '../../../file-uploader.gts';
import {
  AI_READABLE_FILE_ACCEPT,
  TA_MAX_UPLOAD_FILE_SIZE_BYTES,
} from '../../../../utils/tuition-assistance/ai-validation.ts';
import AcceptedBanner from './accepted-banner.gts';
import FlaggedBanner from './flagged-banner.gts';
import NotValidatedBanner from './not-validated-banner.gts';
import type { DocumentType } from '../../../../types/tuition-assistance.ts';
import type { TOC } from '@ember/component/template-only';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

type ChipIntent = 'brand' | 'info' | 'success' | 'warning' | 'danger';

/**
 * Inline status pill shown beneath a document-row title. Composes the
 * shared `.app-status-chip` chrome with the intent-matched
 * `.app-status-panel--{intent}` wash + `.app-status-text-{intent}` color
 * so each variant is a single `@intent` decision. blue/sky → info,
 * violet → brand per the established blue→teal call.
 */
const StatusChip: TOC<{
  Args: { intent: ChipIntent };
  Blocks: { default: [] };
}> = <template>
  {{#let
    (if (eq @intent "brand") "app-status-panel--brand app-status-text-brand")
    (if (eq @intent "info") "app-status-panel--info app-status-text-info")
    (if (eq @intent "success") "app-status-panel--success app-status-text-success")
    (if (eq @intent "warning") "app-status-panel--warning app-status-text-warning")
    (if (eq @intent "danger") "app-status-panel--danger app-status-text-danger")
    as |brand info success warning danger|
  }}
    <span class="app-status-chip {{or brand info success warning danger}}">{{yield}}</span>
  {{/let}}
</template>;

/**
 * Per-row state for a single required document type.
 *
 * Status transitions (matches the Figma "Upload by Type v2" prototype):
 *
 *   empty
 *     ↓ user picks file
 *   uploading            (S3 PUT in flight, brief)
 *     ↓
 *   analyzing            (waiting for AI ServiceResponse)
 *     ↓
 *   valid | flagged | not_validated | error
 *
 * Per the Figma flow:
 *   - "valid" briefly shows a teal border + green check chip then settles to
 *     the gray-bordered "uploaded" look (file chip visible).
 *   - "flagged" keeps the row red and shows a discard / use-anyways banner.
 *   - "accepted" keeps the row amber to reflect the employee override warning.
 *   - "not_validated" keeps the row amber and shows a use-anyways / discard
 *     banner (used when AI couldn't classify the document).
 *   - All states preserve the asset on the application — discards are explicit.
 */
export type DocumentRowStatus =
  | 'empty'
  | 'uploading'
  | 'analyzing'
  | 'valid'
  | 'accepted'
  | 'flagged'
  | 'not_validated'
  | 'error';

/**
 * Why a row is flagged. "content" allows `use anyways` (Figma: receipt missing
 * an amount); "invalid_type" is discard-only (Figma: .mov upload).
 */
export type FlagKind = 'content' | 'invalid_type';

export interface DocumentAssetState {
  asset: TasAssetModel;
  status: DocumentRowStatus;
  feedback: string | null;
  flagKind?: FlagKind;
}

export interface DocumentRowState {
  status: DocumentRowStatus;
  asset: TasAssetModel | null;
  assets: TasAssetModel[];
  assetStates: DocumentAssetState[];
  feedback: string | null;
  flagKind?: FlagKind;
}

interface DocumentRowSignature {
  Args: {
    docType: DocumentType;
    /** Admin-configured custom label. When provided, displayed instead of the
     * i18n translation for the document type. */
    label?: string;
    state: DocumentRowState;
    onUpload: (file: unknown) => void;
    onRemove: (asset?: TasAssetModel) => void;
    onUseAnyways: (asset?: TasAssetModel) => void;
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Border treatment driven by row state. Mirrors the Figma frames:
 *   analyzing → lavender, valid → teal, accepted/not_validated → amber,
 *   flagged/error → red, otherwise neutral gray.
 */
const borderClass = (status: DocumentRowStatus): string => {
  switch (status) {
    case 'analyzing':
    case 'uploading':
      return 'app-border-brand';
    case 'valid':
      return 'app-border-info';
    case 'accepted':
    case 'not_validated':
      return 'app-border-warning';
    case 'flagged':
    case 'error':
      return 'tio-border-danger';
    default:
      return 'tio-border-subtle';
  }
};

const TuitionAssistanceFormsCategorizedUploadDocumentRow: TOC<DocumentRowSignature> = <template>
  <div
    class="tio-col-flush overflow-hidden rounded-xl border tio-bg-surface
      {{borderClass @state.status}}"
    data-status={{@state.status}}
    data-test-doc-row={{@docType}}
    ...attributes
  >
    {{! display:contents — the uploader must span the row body AND the
      full-width rejection strips below it; a real box here would trap the
      strips inside the right-hand button column and shove the button around
      when an error appears. }}
    <FileUploader
      class="contents"
      @didUploadFile={{@onUpload}}
      @accept={{AI_READABLE_FILE_ACCEPT}}
      @enforceAccept={{true}}
      @maxFileSize={{TA_MAX_UPLOAD_FILE_SIZE_BYTES}}
      @hideRejectionErrors={{true}}
      as |uploader|
    >
      <div class="tio-row-start-between gap-3 p-4">
        <div class="tio-col-extra-tight min-w-0">
          <div class="tio-row-tight">
            <span class="tio-text-base-medium">
              {{#if @label}}
                {{@label}}
              {{else}}
                {{t (concat "tas.attachments.document_types." @docType ".label")}}
              {{/if}}
            </span>
            <Popover @placement="bottom-start" @triggerMode="click">
              <:trigger as |pop|>
                <button
                  type="button"
                  class="tio-fg-muted hover:tio-fg-secondary"
                  aria-label={{t (concat "tas.attachments.document_types." @docType ".hint")}}
                  {{pop.register}}
                  {{on "click" pop.toggle}}
                >
                  <PhInfo @size="16" />
                </button>
              </:trigger>
              <:body>
                <div class="max-w-xs p-3 text-sm">
                  {{! Each language file ships an `elements:` array of bullets.
                  Skip for "general" which has no elements defined. }}
                  {{#if (eq @docType "general")}}
                    <p class="font-semibold tio-fg-primary">
                      {{#if @label}}
                        {{@label}}
                      {{else}}
                        {{t (concat "tas.attachments.document_types." @docType ".label")}}
                      {{/if}}
                    </p>
                    <p class="mt-2 tio-fg-secondary">
                      {{t (concat "tas.attachments.document_types." @docType ".hint")}}
                    </p>
                  {{else}}
                    <p class="mb-2 font-semibold tio-fg-primary">
                      {{t "tas.attachments.tooltip_must_include"}}
                    </p>
                    <ul class="ml-4 list-disc tio-fg-secondary">
                      <li>{{t "tas.attachments.tooltip_requirements.student_full_name"}}</li>
                      <li>{{t "tas.attachments.tooltip_requirements.institution"}}</li>
                      {{#if (eq @docType "receipt")}}
                        <li>{{t "tas.attachments.tooltip_requirements.date_of_payment"}}</li>
                        <li>{{t "tas.attachments.tooltip_requirements.items_paid_for"}}</li>
                        <li>{{t "tas.attachments.tooltip_requirements.payment_amount"}}</li>
                        <li>{{t "tas.attachments.tooltip_requirements.payment_method"}}</li>
                      {{else if (eq @docType "certificate")}}
                        <li>{{t "tas.attachments.tooltip_requirements.date_of_completion"}}</li>
                        <li>{{t "tas.attachments.tooltip_requirements.credential_name"}}</li>
                      {{else}}
                        <li>{{t "tas.attachments.tooltip_requirements.dates_of_attendance"}}</li>
                        <li>{{t "tas.attachments.tooltip_requirements.course_name"}}</li>
                        <li>{{t "tas.attachments.tooltip_requirements.grade"}}</li>
                      {{/if}}
                    </ul>
                    <p class="mt-2 tio-fg-secondary">
                      {{t "tas.attachments.tooltip_note"}}
                    </p>
                  {{/if}}
                </div>
              </:body>
            </Popover>
          </div>

          {{! Inline status chip below the title (matches Figma A2/A3). }}
          {{#if (eq @state.status "analyzing")}}
            <StatusChip @intent="brand">
              <PhSparkle @size="12" class="animate-pulse" />
              <span>analyzing</span>
              <span class="flex gap-px">
                <span class="animate-bounce delay-0 duration-1000">.</span>
                <span class="animate-bounce delay-200 duration-1000">.</span>
                <span class="animate-bounce delay-400 duration-1000">.</span>
              </span>
            </StatusChip>
          {{else if (eq @state.status "uploading")}}
            <StatusChip @intent="info">
              <PhCloudArrowUp @size="12" class="animate-pulse" />
              <span>uploading</span>
              <span class="flex gap-px">
                <span class="animate-bounce delay-0 duration-1000">.</span>
                <span class="animate-bounce delay-200 duration-1000">.</span>
                <span class="animate-bounce delay-400 duration-1000">.</span>
              </span>
            </StatusChip>
          {{else if (eq @state.status "valid")}}
            <StatusChip @intent="success">
              <PhCheckCircle @size="12" @weight="fill" />
              {{t "tas.attachments.valid"}}
            </StatusChip>
          {{else if (eq @state.status "accepted")}}
            <StatusChip @intent="warning">
              <PhCheckCircle @size="12" @weight="fill" />
              {{t "tas.attachments.accepted"}}
            </StatusChip>
          {{else if (eq @state.status "flagged")}}
            <StatusChip @intent="danger">
              <PhWarningCircle @size="12" @weight="fill" />
              {{t "tas.attachments.flagged"}}
            </StatusChip>
          {{else if (eq @state.status "error")}}
            <StatusChip @intent="danger">
              <PhXCircle @size="12" @weight="fill" />
              {{t "tas.attachments.error"}}
            </StatusChip>
          {{/if}}

          {{! Uploaded file chips — present whenever assets exist, regardless of
          validation status. Multiple files can belong to one document bucket. }}
          {{#each @state.assets as |asset|}}
            <div
              class="mt-1 tio-inline-row-tight w-fit max-w-full tio-surface-sunken px-2 py-1 text-xs"
            >
              <PhFilePdf @size="14" class="shrink-0 tio-fg-muted" />
              <span class="truncate" title={{asset.filename}}>
                {{asset.filename}}
              </span>
              <button
                type="button"
                class="shrink-0 tio-fg-muted hover:tio-fg-danger"
                aria-label={{t "tas.attachments.remove"}}
                {{on "click" (fn @onRemove asset)}}
              >
                <PhX @size="12" />
              </button>
            </div>
          {{/each}}
        </div>

        {{! Right-side upload control. Full button when empty (Figma A1), icon-only
        once a file exists or is in flight (Figma A2+). }}
        <div class="tio-row-tight shrink-0">
          <input
            id={{uploader.uniqueId}}
            type="file"
            accept={{AI_READABLE_FILE_ACCEPT}}
            {{uploader.queue.selectFile}}
            class="hidden"
            disabled={{or @disabled (eq @state.status "uploading")}}
          />
          {{#if (eq @state.status "empty")}}
            <label
              for={{uploader.uniqueId}}
              class="tio-inline-row cursor-pointer gap-1.5 rounded-md border tio-border-info px-3 py-1.5 tio-text-sm-medium tio-fg-info hover:tio-border-info hover:tio-fg-info-h
                {{if @disabled 'opacity-50 cursor-not-allowed'}}"
              data-test-upload-button
            >
              <PhUploadSimple @size="14" />
              {{t "tas.attachments.upload"}}
            </label>
          {{else}}
            <label
              for={{uploader.uniqueId}}
              class="tio-inline-row-center cursor-pointer rounded-md border tio-border-strong p-1.5 tio-fg-muted hover:tio-border-strong hover:tio-fg-secondary
                {{if @disabled 'opacity-50 cursor-not-allowed'}}"
              aria-label={{t "tas.attachments.upload"}}
              data-test-upload-button-icon
            >
              <PhUploadSimple @size="14" />
            </label>
          {{/if}}
        </div>
      </div>

      {{! Rejected-file strips — full-width below the row body (same panel style
      as the asset error strip further down) so the upload button stays put. }}
      {{#each uploader.typeRejectedFiles as |fileName|}}
        <p
          class="app-status-panel app-status-panel--danger app-status-text-danger border-t border-x-0 border-b-0 px-4 py-3 text-sm last:rounded-b-xl"
          data-test-doc-row-type-error
        >
          {{t
            "file_upload.file_type_not_allowed"
            fileName=fileName
            types=uploader.acceptedTypesDisplay
            default="{fileName} isn't an accepted file type ({types})."
          }}
        </p>
      {{/each}}
      {{#each uploader.sizeRejectedFiles as |fileName|}}
        <p
          class="app-status-panel app-status-panel--danger app-status-text-danger border-t border-x-0 border-b-0 px-4 py-3 text-sm last:rounded-b-xl"
          data-test-doc-row-size-error
        >
          {{t
            "file_upload.file_too_large"
            fileName=fileName
            maxSize=uploader.maxFileSizeMb
            default="{fileName} is too large. Maximum file size is {maxSize}MB."
          }}
        </p>
      {{/each}}
    </FileUploader>

    {{! Expanded banners. Rendered below the row body (Figma A10 / B4 / 4359
      and 4362 frames). The banner owns its own border bar + actions. }}
    {{#each @state.assetStates as |assetState|}}
      {{#if (eq assetState.status "flagged")}}
        <FlaggedBanner
          @filename={{assetState.asset.filename}}
          @reason={{assetState.feedback}}
          @flagKind={{if (eq assetState.flagKind "invalid_type") "invalid_type" "content"}}
          @onDiscard={{fn @onRemove assetState.asset}}
          @onUseAnyways={{fn @onUseAnyways assetState.asset}}
        />
      {{else if (eq assetState.status "not_validated")}}
        <NotValidatedBanner
          @filename={{assetState.asset.filename}}
          @onUseAnyways={{fn @onUseAnyways assetState.asset}}
          @onDiscard={{fn @onRemove assetState.asset}}
        />
      {{else if (eq assetState.status "accepted")}}
        <AcceptedBanner @filename={{assetState.asset.filename}} />
      {{else if (eq assetState.status "error")}}
        <p
          class="app-status-panel app-status-panel--danger app-status-text-danger border-t border-x-0 border-b-0 px-4 py-3 text-sm last:rounded-b-xl"
        >
          {{assetState.asset.filename}}:
          {{assetState.feedback}}
        </p>
      {{/if}}
    {{/each}}
  </div>
</template>;

export default TuitionAssistanceFormsCategorizedUploadDocumentRow;
